/**
 * Pure helpers for the Dashboard page (`design/Syntra/dashboard.html`).
 *
 * The design roll-ups reuse the same per-property engine as the Properties and
 * Financing pages, so the numbers match across the app:
 *
 *   - `computePropertyMetrics` → per-property ARV / cash flow / equity.
 *   - `computeOwnedLoanInfo` + `nextEmiDate` → monthly payment + EMI date.
 *
 * This module only adds the dashboard-specific aggregation (KPI roll-up,
 * portfolio mix by strategy, acquisition funnel by stage) and the compact money
 * formatter the design uses.
 */
import {
  computeOwnedLoanInfo,
  fmtMaturityDate,
  nextEmiDate,
} from "@/components/financing/financing-metrics";
import {
  computePropertyMetrics,
  type PropertyMetrics,
  type PropertyStatus,
} from "@/components/properties/property-metrics";
import type { OwnedProperty } from "@/hooks/use-owned-properties";
import { isTerminalPipeline } from "@/lib/pipeline-display";
import type { PipelineRow, Strategy } from "@/types/pipeline";

/* ────────────────────────────────────────────────────────────────────────── *
 * Formatting (mirrors the design's fmtMoney / fmtMoneyShort)
 * ────────────────────────────────────────────────────────────────────────── */

export function fmtMoney(n: number): string {
  const sign = n < 0 ? "-" : "";
  return sign + "$" + Math.abs(Math.round(n)).toLocaleString();
}

export function fmtMoneyShort(n: number): string {
  const a = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (a >= 1e6) return sign + "$" + (a / 1e6).toFixed(a >= 1e7 ? 0 : 1) + "M";
  if (a >= 1e3) return sign + "$" + Math.round(a / 1e3) + "K";
  return fmtMoney(n);
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Strategy display (BRRRR = blue, Flip = amber, Rental = green)
 * ────────────────────────────────────────────────────────────────────────── */

export type StrategyKey = "brrrr" | "flip" | "rental";

export function stratKey(strategy: Strategy | null | undefined): StrategyKey {
  const k = String(strategy ?? "").toLowerCase();
  if (k === "flip") return "flip";
  if (k === "rental") return "rental";
  return "brrrr";
}

export const STRAT_LABEL: Record<StrategyKey, string> = {
  brrrr: "BRRRR",
  flip: "Flip",
  rental: "Rental",
};

/** CSS color per strategy — used for donut arcs and dots (dynamic values). */
export const STRAT_COLOR: Record<StrategyKey, string> = {
  brrrr: "#3b82f6",
  flip: "hsl(var(--warning))",
  rental: "hsl(var(--success))",
};

/** Order strategies appear in the mix legend (matches the design). */
export const STRAT_ORDER: StrategyKey[] = ["brrrr", "flip", "rental"];

/* ────────────────────────────────────────────────────────────────────────── *
 * Per-property decoration
 * ────────────────────────────────────────────────────────────────────────── */

export interface DecoratedProperty {
  item: OwnedProperty;
  metrics: PropertyMetrics;
}

export function decorate(item: OwnedProperty): DecoratedProperty {
  return {
    item,
    metrics: computePropertyMetrics({
      settings: item.settings,
      fallbackPurchase: item.purchasePrice ?? 0,
      strategy: item.strategy,
      rehabStartDate: item.rehabStartDate,
      ownedDate: item.ownedDate,
      actualMonthlyRent: item.actualMonthlyRent,
      loan: item.loan,
      rehabLineItems: item.rehabLineItems,
    }),
  };
}

/* ────────────────────────────────────────────────────────────────────────── *
 * KPI roll-up (Portfolio value · Total equity · Monthly cash flow)
 * ────────────────────────────────────────────────────────────────────────── */

export interface DashboardKpis {
  /** Σ ARV across every owned property (includes sold). */
  totalValue: number;
  /** Σ (ARV − purchase) across every owned property (includes sold). */
  totalEquity: number;
  /** Σ monthly cash flow across active properties (excludes sold). */
  totalCashFlow: number;
}

export function aggregateDashboardKpis(
  entries: Array<{ metrics: PropertyMetrics; status: PropertyStatus }>,
): DashboardKpis {
  let totalValue = 0;
  let totalEquity = 0;
  let totalCashFlow = 0;
  for (const { metrics, status } of entries) {
    totalValue += metrics.arv;
    totalEquity += metrics.arv - metrics.purchase;
    if (status !== "sold") totalCashFlow += metrics.monthlyCashFlow;
  }
  return { totalValue, totalEquity, totalCashFlow };
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Portfolio mix by strategy (donut + legend)
 * ────────────────────────────────────────────────────────────────────────── */

export interface MixSlice {
  key: StrategyKey;
  label: string;
  color: string;
  count: number;
  /** Σ ARV for this strategy. */
  vol: number;
  /** Share of owned properties, 0–100 (rounded). */
  pct: number;
}

export function buildPortfolioMix(entries: DecoratedProperty[]): {
  slices: MixSlice[];
  total: number;
} {
  const groups: Partial<Record<StrategyKey, { count: number; vol: number }>> = {};
  for (const { item, metrics } of entries) {
    const k = stratKey(item.strategy);
    const g = (groups[k] ??= { count: 0, vol: 0 });
    g.count += 1;
    g.vol += metrics.arv;
  }
  const total = entries.length;
  const slices = STRAT_ORDER.filter((k) => groups[k]).map((k) => {
    const g = groups[k]!;
    return {
      key: k,
      label: STRAT_LABEL[k],
      color: STRAT_COLOR[k],
      count: g.count,
      vol: g.vol,
      pct: total > 0 ? Math.round((g.count / total) * 100) : 0,
    };
  });
  return { slices, total };
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Acquisition funnel by stage (composition bar + chips)
 * ────────────────────────────────────────────────────────────────────────── */

export const FALLBACK_STAGE_COLOR = "#8a93a3";

interface SavedPropertyRow {
  property_data: unknown;
}

export interface FunnelStage {
  id: string;
  name: string;
  color: string;
  count: number;
  /** Σ list price of the deals in this stage. */
  vol: number;
}

export interface FunnelData {
  stages: FunnelStage[];
  /** Total active deals across non-terminal stages. */
  totalActive: number;
  /** Σ list price across non-terminal stages. */
  totalVolume: number;
}

/**
 * Build the acquisition funnel from the kanban pipelines. Terminal columns
 * (Closed Won / Closed Lost) are excluded — matches the design, which drops
 * the two closed stages from both the chips and the active count.
 */
export function buildFunnel(
  pipelines: PipelineRow[],
  getPropertiesByPipeline: (pipeline: PipelineRow) => SavedPropertyRow[],
): FunnelData {
  const stages: FunnelStage[] = [];
  let totalActive = 0;
  let totalVolume = 0;
  for (const pipeline of pipelines) {
    if (isTerminalPipeline(pipeline)) continue;
    const rows = getPropertiesByPipeline(pipeline);
    const vol = rows.reduce((sum, r) => {
      const price = Number((r.property_data as { price?: number })?.price) || 0;
      return sum + price;
    }, 0);
    stages.push({
      id: pipeline.id,
      name: pipeline.title,
      color: pipeline.color ?? FALLBACK_STAGE_COLOR,
      count: rows.length,
      vol,
    });
    totalActive += rows.length;
    totalVolume += vol;
  }
  return { stages, totalActive, totalVolume };
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Portfolio table rows (cash flow · monthly payment · EMI date)
 * ────────────────────────────────────────────────────────────────────────── */

export interface PortfolioRow {
  id: string;
  addr1: string;
  addr2: string;
  strategyKey: StrategyKey;
  strategyLabel: string;
  status: PropertyStatus;
  cashFlow: number;
  /** Monthly loan payment, formatted; "—" when unfinanced. */
  monthly: string;
  /** Next EMI date, formatted; "—" when incomputable. */
  emi: string;
}

const STATUS_LABEL: Record<PropertyStatus, string> = {
  "in_rehab": "In Rehab",
  owned: "Owned",
  sold: "Sold",
};

export function statusLabel(status: PropertyStatus): string {
  return STATUS_LABEL[status];
}

export function buildPortfolioRow({ item, metrics }: DecoratedProperty): PortfolioRow {
  const pd = item.property;
  const cityState = [pd.city, pd.state].filter(Boolean).join(", ");
  const loan = computeOwnedLoanInfo({
    settings: item.settings,
    strategy: item.strategy,
    fallbackPurchase: item.purchasePrice ?? 0,
    loan: item.loan,
  });
  const key = stratKey(item.strategy);
  return {
    id: item.id,
    addr1: pd.address ?? "Property",
    addr2: cityState,
    strategyKey: key,
    strategyLabel: STRAT_LABEL[key],
    status: item.status,
    cashFlow: metrics.monthlyCashFlow,
    monthly: loan.financed && loan.monthly > 0 ? fmtMoney(loan.monthly) : "—",
    emi: fmtMaturityDate(nextEmiDate(loan.loanDate)),
  };
}
